import React, { useState, useEffect } from 'react';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

import Icon from '../../../components/AppIcon';

const LocationSection = ({ formData, setFormData, errors }) => {
  const dayOptions = [
    { value: 'monday', label: 'Monday' },
    { value: 'tuesday', label: 'Tuesday' },
    { value: 'wednesday', label: 'Wednesday' },
    { value: 'thursday', label: 'Thursday' },
    { value: 'friday', label: 'Friday' },
    { value: 'saturday', label: 'Saturday' },
    { value: 'sunday', label: 'Sunday' }
  ];

  const handleInputChange = (field, value) => {
    if (field?.startsWith('openingHours.')) {
      const day = field?.split('.')?.[1];
      setFormData(prev => ({
        ...prev,
        location: {
          ...prev?.location,
          openingHours: {
            ...prev?.location?.openingHours,
            [day]: value
          }
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        location: {
          ...prev?.location,
          [field]: value
        }
      }));
    }
  };

  const handleOnlineToggle = (isOnline) => {
    setFormData(prev => ({
      ...prev,
      location: {
        ...prev?.location,
        isOnline: isOnline,
        // Clear physical location fields if going online
        address: isOnline ? '' : prev?.location?.address,
        city: isOnline ? '' : prev?.location?.city,
        postcode: isOnline ? '' : prev?.location?.postcode,
        fullAddress: isOnline ? '' : prev?.location?.fullAddress,
        // Keep showMap true for business detail page
        showMap: !isOnline
      }
    }));
  };

  const generateFullAddress = () => {
    const { address, city, postcode } = formData?.location || {};
    if (address && city && postcode) {
      const fullAddress = `${address}, ${city}, ${postcode}`;
      handleInputChange('fullAddress', fullAddress);
    }
  };

  // Auto-generate full address when components change
  React.useEffect(() => {
    if (!formData?.location?.isOnline) {
      generateFullAddress();
    }
  }, [formData?.location?.address, formData?.location?.city, formData?.location?.postcode]);

  return (
    <div className="bg-card rounded-lg p-6 space-y-6">
      <div className="border-b border-border pb-4">
        <h2 className="text-xl font-heading font-semibold text-foreground">Location & Contact</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Add your location details and opening hours for your business detail page
        </p>
      </div>
      {/* Online/Physical Toggle */}
      <div className="space-y-4">
        <div className="flex items-center space-x-4">
          <Checkbox
            checked={formData?.location?.isOnline || false}
            onChange={handleOnlineToggle}
            label="This is an online service"
          />
        </div>

        {formData?.location?.isOnline && (
          <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Icon name="Monitor" size={16} className="text-accent mt-0.5" />
              <div>
                <h3 className="font-body font-medium text-foreground text-sm">Online Service</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  Your service will be marked as online and won't require a physical address.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Physical Location Fields */}
      {!formData?.location?.isOnline && (
        <div className="space-y-4">
          <h3 className="font-medium text-foreground">Physical Location</h3>
          
          <Input
            label="Street Address"
            value={formData?.location?.address || ''}
            onChange={(e) => handleInputChange('address', e?.target?.value)}
            placeholder="123 Wellness Street"
            error={errors?.['location.address']}
            required
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="City"
              value={formData?.location?.city || ''}
              onChange={(e) => handleInputChange('city', e?.target?.value)}
              placeholder="London"
              error={errors?.['location.city']}
              required
            />

            <Input
              label="Postcode"
              value={formData?.location?.postcode || ''}
              onChange={(e) => handleInputChange('postcode', e?.target?.value?.toUpperCase())}
              placeholder="SW1A 1AA"
              error={errors?.['location.postcode']}
              required
            />
          </div>

          {/* Full Address Preview */}
          {formData?.location?.fullAddress && (
            <div className="p-3 bg-secondary/10 rounded-lg">
              <div className="flex items-center space-x-2">
                <Icon name="MapPin" size={16} className="text-primary" />
                <span className="text-sm font-medium text-foreground">Full Address:</span>
                <span className="text-sm text-muted-foreground">{formData?.location?.fullAddress}</span>
              </div>
            </div>
          )}

          {/* Map Display Option */}
          <div className="space-y-2">
            <Checkbox
              checked={formData?.location?.showMap !== false} // Default to true
              onChange={(checked) => handleInputChange('showMap', checked)}
              label="Show map on business detail page"
            />
            <p className="text-xs text-muted-foreground ml-6">
              Display a map on your business detail page to help clients find you
            </p>
          </div>
        </div>
      )}
      {/* Opening Hours */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-medium text-foreground">Opening Hours</h3>
          <button
            type="button"
            onClick={() => {
              // Set default hours for all days
              const defaultHours = '9:00 AM - 5:00 PM';
              dayOptions?.forEach(day => {
                handleInputChange(`openingHours.${day?.value}`, defaultHours);
              });
            }}
            className="text-sm text-primary hover:text-primary/80 font-medium"
          >
            Set Default Hours
          </button>
        </div>
        
        <p className="text-xs text-muted-foreground">
          Add your opening hours to display on your business detail page. Leave blank for "Closed" or "By appointment only"
        </p>

        <div className="space-y-3">
          {dayOptions?.map(day => (
            <div key={day?.value} className="grid grid-cols-3 gap-4 items-center">
              <label className="text-sm font-medium text-foreground capitalize">
                {day?.label}
              </label>
              <div className="col-span-2">
                <Input
                  value={formData?.location?.openingHours?.[day?.value] || ''}
                  onChange={(e) => handleInputChange(`openingHours.${day?.value}`, e?.target?.value)}
                  placeholder="e.g., 9:00 AM - 7:00 PM, Closed, By appointment"
                  className="text-sm"
                />
              </div>
            </div>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="flex flex-wrap gap-2 pt-2">
          <button
            type="button"
            onClick={() => {
              // Set weekday hours
              ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']?.forEach(day => {
                handleInputChange(`openingHours.${day}`, '9:00 AM - 7:00 PM');
              });
            }}
            className="text-xs bg-secondary/20 text-secondary-foreground px-3 py-1 rounded-full hover:bg-secondary/30 transition-colors"
          >
            Set Weekdays (9-7)
          </button>
          
          <button
            type="button"
            onClick={() => {
              handleInputChange('openingHours.saturday', '10:00 AM - 5:00 PM');
            }}
            className="text-xs bg-secondary/20 text-secondary-foreground px-3 py-1 rounded-full hover:bg-secondary/30 transition-colors"
          >
            Saturday (10-5)
          </button>
          
          <button
            type="button"
            onClick={() => {
              handleInputChange('openingHours.sunday', 'Closed');
            }}
            className="text-xs bg-secondary/20 text-secondary-foreground px-3 py-1 rounded-full hover:bg-secondary/30 transition-colors"
          >
            Sunday Closed
          </button>
        </div>
      </div>
      {/* Location Tips */}
      <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={16} className="text-accent mt-0.5" />
          <div>
            <h3 className="font-body font-medium text-foreground text-sm">Location Tips</h3>
            <ul className="text-xs text-muted-foreground mt-1 space-y-1">
              <li>• Provide accurate address information for better local search visibility</li>
              <li>• Include opening hours to help clients know when you're available</li>
              <li>• Enable map display to help clients find your location easily</li>
              <li>• For online services, highlight the convenience of remote access</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationSection;